import type { StorageLike } from './progress-store';

/**
 * The pieces the player owns, and which one to put back under their hands.
 *
 * ## Why this is not in `JourneyProgress`
 *
 * Progression is one key per piece, `virtuosa:journey:<morceauId>`, with no
 * registry — nothing in the product can answer "which pieces exist". Three
 * facts made a separate key the only workable place:
 *
 * - **there is no index.** Building the queue out of the journey keys would
 *   mean enumerating `localStorage` by prefix at every boot;
 * - **the title is not in the payload.** `JourneyProgress` carries neither
 *   title nor composer, so the line that offers the piece could not name it;
 * - **`phrasePlanHash` invalidates in silence.** A transposed piece makes
 *   `load()` return `null`, so the piece would drop out of the queue with no
 *   event and no explanation — breaking "tu n'auras rien à rattraper" from
 *   the other end. Living in another key, the queue survives it.
 *
 * It also isolates the quota: writing the queue into `JourneyProgress` would
 * make one `QuotaExceededError` take down the whole save of the piece.
 *
 * ## The cap of one is in the data, not in the view
 *
 * `due()` returns **one entry or nothing**, never an array. A ceiling that
 * only exists in the rendering is a ceiling someone will ask to lift — "show
 * me the others" — and then it is a list, and the count it implies is a
 * backlog. If the view can never receive two, the list cannot be built.
 *
 * `offer()` extends the same rule to the piece still being climbed: two
 * candidates, one slot, arbitrated here. See `Offer`.
 */

const KEY = 'virtuosa:repertoire';

/**
 * The piece being climbed right now, and why it is a second key rather than
 * a field of `JourneyProgress`.
 *
 * The date was never the missing part: the journey document already carries
 * two (`TraversalProgress.at`, `PhraseProgress.lastPlayedAt`). What it does
 * not carry is a **title** to name the piece and a **`pieceId`** to open it,
 * and there is no index that would say which document is the latest without
 * walking `localStorage` by prefix. Those are the same three facts that keep
 * the queue above out of `JourneyProgress`.
 *
 * One entry, overwritten. A history of what was worked on is a list, and the
 * list is the thing this module exists not to build.
 */
const WORKING_KEY = 'virtuosa:working';

/**
 * Days between one crossing and the next offer.
 *
 * Expanding, because the point of the spacing is that each successful recall
 * buys a longer silence. The ladder has **no visual home**: not the current
 * rung, not the next date, not the interval. Naming it would turn keeping a
 * piece into a schedule to keep up with, which is the one thing the
 * repertoire is meant not to be.
 */
const SPACING_DAYS = [1, 3, 7, 14, 28, 56, 112, 224] as const;

const DAY_MS = 86_400_000;

export type RepertoireEntry = {
  /** Journey identity, i.e. `sourceMidiId` possibly suffixed by transposition. */
  readonly morceauId: string;
  /** Catalogue identity — the only thing that can load the piece back. */
  readonly pieceId: string;
  readonly title: string;
  /**
   * How long one crossing takes at the tempo it was held at, in seconds.
   *
   * Stored because it is what the offer says, and it cannot be derived
   * without loading the piece — which is what the offer is asking about.
   */
  readonly seconds: number;
  /** Index into `SPACING_DAYS`, clamped at the top. */
  readonly rung: number;
  /** Epoch ms of the last crossing. */
  readonly lastPlayedAt: number;
};

/**
 * The piece the player is in the middle of, i.e. the last one the journey was
 * opened on.
 *
 * `pieceId` is required and not optional: an offer that cannot open anything
 * is worse than no offer, and a private import has no catalogue entry to
 * open. Those pieces stay reachable from « Mes morceaux », the one screen
 * that knows what to do with an entry that is not openable.
 */
export type WorkingPiece = {
  readonly morceauId: string;
  readonly pieceId: string;
  readonly title: string;
  /** Epoch ms of the last time the journey was opened on it. */
  readonly at: number;
};

/**
 * The one thing to put back under the player's hands, or nothing.
 *
 * Two kinds, **one slot**. `replay` is a kept piece whose silence has run
 * out, `resume` is the piece still being climbed. The arbitration lives here
 * and not in the view, for the same reason the cap of one does: a second line
 * on that screen is a list, and a list implies a backlog.
 *
 * `replay` wins when both stand. It is dated — it fires on the day the ladder
 * says and not before — where the piece in progress is there every session.
 * The other order would show the climb every day and the ladder never, which
 * is the expanding spacing removed by scheduling rather than by decision.
 */
export type Offer =
  | { readonly kind: 'replay'; readonly entry: RepertoireEntry }
  | { readonly kind: 'resume'; readonly piece: WorkingPiece };

export class Repertoire {
  private readonly storage: StorageLike;

  /**
   * Offered this session and not taken. In memory only: a refusal is not a
   * decision, so it must not survive the tab. But within one session, an
   * offer that came back would turn "one at a time" into "one at a time, on
   * a loop" — a list served in series.
   */
  private readonly declined = new Set<string>();

  constructor(storage: StorageLike) {
    this.storage = storage;
  }

  /**
   * The piece has been played through: it is kept from now on.
   *
   * Idempotent, and does **not** reset the rung — a piece re-entering the
   * repertoire it never really left keeps its spacing.
   */
  remember(
    entry: Pick<RepertoireEntry, 'morceauId' | 'pieceId' | 'title' | 'seconds'>,
    at: number,
  ): void {
    if (entry.title === '' || entry.seconds <= 0) {
      return;
    }

    const kept = this.read();
    const existing = kept.find((e) => e.morceauId === entry.morceauId);

    this.write([
      ...kept.filter((e) => e.morceauId !== entry.morceauId),
      {
        ...entry,
        rung: existing?.rung ?? 0,
        lastPlayedAt: at,
      },
    ]);
  }

  /**
   * A kept piece has just been crossed again: the next silence gets longer.
   *
   * A no-op on a piece that is not kept — crossing a piece still being
   * climbed is phase 2's business, not this one's.
   */
  playedThrough(morceauId: string, at: number, seconds: number): void {
    const kept = this.read();
    const entry = kept.find((e) => e.morceauId === morceauId);

    if (entry === undefined) {
      return;
    }

    this.write([
      ...kept.filter((e) => e.morceauId !== morceauId),
      {
        ...entry,
        // Refreshed, because the tempo keeps climbing inside the repertoire.
        // Entering at 0.45 and stopping at 1 halves the crossing: an offer
        // still quoting the first figure asks for twice the evening it needs,
        // and the duration is the only number on that line.
        seconds: seconds > 0 ? seconds : entry.seconds,
        rung: Math.min(entry.rung + 1, SPACING_DAYS.length - 1),
        lastPlayedAt: at,
      },
    ]);
  }

  /**
   * The one piece to offer, or nothing.
   *
   * Among those whose silence has run out, the one **untouched longest**
   * wins. Sorting by rung instead — least consolidated first — reads better
   * and starves in practice: a piece at rung 0 comes due again after 1, then
   * 3, then 7 days, so with four pieces in rotation the ones high on the
   * ladder are never reached, which is the opposite of what an expanding
   * ladder is for.
   */
  due(at: number): RepertoireEntry | null {
    const ready = this.read()
      .filter((e) => !this.declined.has(e.morceauId))
      .filter((e) => at - e.lastPlayedAt >= SPACING_DAYS[e.rung] * DAY_MS)
      .sort((a, b) => a.lastPlayedAt - b.lastPlayedAt);

    return ready[0] ?? null;
  }

  /** Offered and not taken. No button says this — not clicking it does. */
  decline(morceauId: string): void {
    this.declined.add(morceauId);
  }

  /**
   * The journey was just opened on this piece: it is the one to come back to.
   *
   * Recorded on every mount of the journey, which is coarser than "played" on
   * purpose. Coming back to the screen **is** the act being resumed, and a
   * finer signal would mean the journey reporting each of its own writes for
   * a line that only ever names one piece.
   */
  working(piece: Omit<WorkingPiece, 'at'>, at: number): void {
    if (piece.morceauId === '' || piece.pieceId === '' || piece.title === '') {
      return;
    }

    try {
      this.storage.setItem(WORKING_KEY, JSON.stringify({ ...piece, at }));
    } catch {
      // As in `write`: a full quota costs the next offer and nothing else.
    }
  }

  /**
   * The same pointer, but rebuilt from the account instead of this browser.
   *
   * Only when it is more recent than what is already here. The local pointer
   * is written by the journey itself and is therefore the truer of the two:
   * the account only knows when a piece was last *served*, which a free-play
   * reading also counts. Taking the newer of the two can only ever point at
   * a piece the player did touch, on some device, more recently than the one
   * standing — which is exactly what the line claims.
   *
   * A date that does not parse writes nothing. Storing it would produce a
   * row `isWorking` then rejects, i.e. a seed that erases a good pointer.
   */
  seedWorking(piece: Omit<WorkingPiece, 'at'>, at: number): void {
    if (!Number.isFinite(at)) {
      return;
    }

    const existing = this.readWorking();

    if (existing !== null && existing.at >= at) {
      return;
    }

    this.working(piece, at);
  }

  /**
   * The single act to offer on an empty screen, or nothing.
   *
   * Not two calls the caller then has to arbitrate: the empty screen would be
   * the third place to hold a rule that only makes sense once, and the first
   * one able to render both at the same time.
   */
  offer(at: number): Offer | null {
    const entry = this.due(at);

    if (entry !== null) {
      return { kind: 'replay', entry };
    }

    const piece = this.readWorking();

    if (piece === null) {
      return null;
    }

    // A kept piece is not being climbed any more: the ladder owns it, and it
    // comes back on the day the ladder says. Offering it in between is the
    // spacing removed — and it would be offered *every* session, since a
    // piece that just entered the repertoire is also the last one worked on.
    const kept = this.read().some((e) => e.morceauId === piece.morceauId);

    return kept ? null : { kind: 'resume', piece };
  }

  private readWorking(): WorkingPiece | null {
    try {
      const raw = this.storage.getItem(WORKING_KEY);

      if (raw === null) {
        return null;
      }

      const parsed: unknown = JSON.parse(raw);

      return isWorking(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private read(): readonly RepertoireEntry[] {
    try {
      // `getItem` is inside the try, not outside: on a browser with site
      // data blocked, reading the property throws rather than returning null.
      const raw = this.storage.getItem(KEY);

      if (raw === null) {
        return [];
      }

      const parsed: unknown = JSON.parse(raw);

      // Entries are independent of each other, so one malformed row drops
      // itself rather than the whole repertoire. That is the opposite of
      // `progress-store`, where the payload is a single indivisible fact.
      return Array.isArray(parsed)
        ? parsed.filter(isEntry).map(normalise)
        : [];
    } catch {
      return [];
    }
  }

  private write(entries: readonly RepertoireEntry[]): void {
    try {
      this.storage.setItem(KEY, JSON.stringify(entries));
    } catch {
      // A full quota costs the next offer, nothing else. The piece's own
      // progression lives under another key and is written by another call.
    }
  }
}

function isEntry(value: unknown): value is RepertoireEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const e = value as Record<string, unknown>;

  return (
    typeof e.morceauId === 'string' &&
    e.morceauId !== '' &&
    typeof e.pieceId === 'string' &&
    e.pieceId !== '' &&
    // An empty title would render as `Rejouer  · ~3 min`.
    typeof e.title === 'string' &&
    e.title !== '' &&
    typeof e.seconds === 'number' &&
    e.seconds > 0 &&
    typeof e.rung === 'number' &&
    typeof e.lastPlayedAt === 'number'
  );
}

function isWorking(value: unknown): value is WorkingPiece {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const p = value as Record<string, unknown>;

  return (
    typeof p.morceauId === 'string' &&
    p.morceauId !== '' &&
    typeof p.pieceId === 'string' &&
    p.pieceId !== '' &&
    // An empty title would render as `Reprendre  · Phrase 7 sur 19`.
    typeof p.title === 'string' &&
    p.title !== '' &&
    typeof p.at === 'number' &&
    Number.isFinite(p.at)
  );
}

/**
 * Bring a well-formed row back into the range the rest of the code assumes.
 *
 * A `rung` outside the ladder makes `SPACING_DAYS[rung]` undefined, so the
 * due test compares against `NaN` and is false for ever: the row survives
 * every read, holds its place in the array, and is never offered again —
 * invisible rather than dropped, which is worse, because nothing repairs it.
 * A `lastPlayedAt` in the future (a clock set wrong) does the same. Clamping
 * loses nothing; rejecting would.
 */
function normalise(entry: RepertoireEntry): RepertoireEntry {
  const rung = Math.min(
    Math.max(0, Math.trunc(entry.rung) || 0),
    SPACING_DAYS.length - 1,
  );
  const lastPlayedAt = Number.isFinite(entry.lastPlayedAt)
    ? entry.lastPlayedAt
    : 0;

  return rung === entry.rung && lastPlayedAt === entry.lastPlayedAt
    ? entry
    : { ...entry, rung, lastPlayedAt };
}

/**
 * A repertoire that keeps nothing, for when there is no storage to keep it
 * in — a browser with site data blocked, or a server render.
 *
 * It degrades instead of throwing because every caller sits on a path where
 * an exception costs something else: in `handleFinished` it would abort the
 * writes that record a six-minute crossing, and in the studio's empty screen
 * it fires from a `requestAnimationFrame`, outside any error boundary.
 */
const NOWHERE: StorageLike = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

let shared: Repertoire | null = null;

/**
 * The session's repertoire. One instance, because `declined` is session
 * state: a second instance would re-offer a piece that was just passed over.
 */
export function repertoire(): Repertoire {
  shared ??= new Repertoire(available());

  return shared;
}

function available(): StorageLike {
  // A module-level singleton is a *process*-level singleton on the server,
  // where `declined` would be shared between users. Nothing reaches this on
  // a server render today; the guard keeps it that way.
  if (typeof window === 'undefined') {
    return NOWHERE;
  }

  try {
    return window.localStorage ?? NOWHERE;
  } catch {
    return NOWHERE;
  }
}
